import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/lib/i18n";

/**
 * Rechter kaartje — vaste glas-op-maat-promo. Vaste content in de site (niet via admin),
 * zodat de hero direct rendert zonder Firestore-read (geen laad-flits). Aanpassen = deze
 * constanten wijzigen.
 */
const HERO_CONTENT = {
  eyebrow: "Glas op maat",
  title: "Op de millimeter",
  subhead: "Direct uw prijs.",
  description: "Kies glassoort, maat en dikte — plus boringen of geslepen randen — en zie meteen uw prijs. Verzekerd op maat bezorgd.",
  cta: { label: "Stel samen", to: "/glassoorten" },
  image: "/hero-links.webp", // sfeerfoto glas op maat: klassieke grachtenpandgevel met kozijnen/beglazing
};

// Piepklein (24px breed), vooraf uitgesmeerd voorproefje van dezelfde foto, base64 inline — dus
// met NUL netwerk-wachttijd al zichtbaar. Zonder dit zag je een egale navy vlek (bg-[#013878])
// tot de echte foto binnen was: een harde kleur-naar-foto-flits. Nu zie je vanaf de allereerste
// paint al een wazige versie van dezelfde foto, die vervangen wordt zodra de scherpe versie laadt.
const HERO_LQIP = "data:image/webp;base64,UklGRmQAAABXRUJQVlA4IFgAAACwBACdASoYACAAPu1kpk2ppaMiMBgMATAdiWMAuwAMXpL1HQJ57c8Wuud6IyLgAP7jTYm5Ga5P32kuhmOrGFp6TKKWnWp85W2t/lFLh+h77gwLeGhINlQA";

/** Tweede blok — het assortiment voor de vakman. `image` = foto in /public; laadt 'ie niet, dan
 *  valt het blok terug op de egale navy-look.
 *
 *  GEEN vanaf-prijs meer (20-09-2026, besluit Thijs). Hier stond "vanaf € 3,03" — het goedkoopste
 *  kokertje kit. Op de belangrijkste plek van de site las dat als een doe-het-zelfwinkel, terwijl
 *  dit juist het blok is dat het vakmanschap moet tonen. Bij gereedschap zou het nog schever zijn
 *  geworden: het goedkoopste artikel in die groep kost € 0,65. De reviewsterren blijven wél staan:
 *  die geven een reden om te vertrouwen zonder een bedrag te beloven.
 *
 *  Foto (20-09-2026): werkbank met zuignap, glassnijder, plamuurmes met stopverf, klemrubber,
 *  beglazingsblokjes en een ruit — allemaal dingen die we écht verkopen. Verving de kitfoto, die
 *  op de belangrijkste plek van de site "doe-het-zelfwinkel" uitstraalde.
 *
 *  Geen verloop of sluier nodig: de foto is links zelf donker genoeg. Gemeten op het bestand
 *  (20-09-2026): wit haalt 11,9:1 op de kop, 7,4:1 op het label en 13,5:1 op de link — ruim boven
 *  de 4,5:1 die WCAG vraagt. Komt er ooit een lichtere foto, meet dan opnieuw en zet zo nodig een
 *  lokaal verloop terug; een eerdere lichtgrijze versie haalde maar 2,2:1. */
const LEFT_PROMO = {
  chip: "Direct uit voorraad",
  title: "Glasgereedschap",
  subtitle: "Voor wie met glas werkt.",
  ctaLabel: "Bekijk het gereedschap",
  ctaTo: "/categorie/glasartikelen/gereedschappen",
  image: "/hero-werkbank.webp",
};

/**
 * Twee-koloms hero met vaste content (geen admin). Links: categorie-promo (gereedschap).
 * Rechts: glas-op-maat-kernpropositie met zwevend wit info-vak.
 */
export function HeroSection() {
  const t = useT();

  // Mobiele auto-carousel (Coolblue-stijl): de 2 blokken als swipebare slides met
  // progress-balkjes; elke 5s automatisch door. Gebruikers-swipe wint (interval reset).
  const trackRef = useRef<HTMLDivElement>(null);
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const mobile = window.matchMedia("(max-width: 767px)");
    const onScroll = (): void => {
      setSlide(Math.round(track.scrollLeft / track.clientWidth));
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    const id = setInterval(() => {
      if (!mobile.matches || document.hidden) return;
      const next = (Math.round(track.scrollLeft / track.clientWidth) + 1) % 2;
      track.scrollTo({ left: next * track.clientWidth, behavior: "smooth" });
    }, 5000);
    return () => { track.removeEventListener("scroll", onScroll); clearInterval(id); };
  }, []);

  const leftChip     = t(LEFT_PROMO.chip);
  const leftTitle    = t(LEFT_PROMO.title);
  const leftSub      = t(LEFT_PROMO.subtitle);
  const leftCtaLabel = t(LEFT_PROMO.ctaLabel);
  const leftCtaTo    = LEFT_PROMO.ctaTo;

  const rightEyebrow  = t(HERO_CONTENT.eyebrow);
  const rightTitle    = t(HERO_CONTENT.title);
  const rightSubhead  = t(HERO_CONTENT.subhead);
  const rightDesc     = t(HERO_CONTENT.description);
  const rightCtaLabel = t(HERO_CONTENT.cta.label);
  const rightCtaTo    = HERO_CONTENT.cta.to;
  const rightImg      = HERO_CONTENT.image;

  return (
    <section aria-label="Hero" className="mx-auto mt-3 max-w-[1600px] px-4 md:mt-6">
      {/* Mobiel: swipebare auto-carousel; md+: 2-koloms grid (ongewijzigd) */}
      <div ref={trackRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:grid md:snap-none md:grid-cols-2 md:overflow-visible md:h-[clamp(480px,58vh,680px)]">
        {/* Primair blok — glas op maat (de kernpropositie): sfeerfoto met wit info-vak. */}
        <div
          className="relative h-[420px] w-full shrink-0 snap-start overflow-hidden bg-[#013878] bg-cover bg-center sm:h-[clamp(480px,58vh,680px)] md:h-auto md:w-auto md:shrink"
          style={{ backgroundImage: `url(${HERO_LQIP})` }}
        >
          {/* Achtergrond-sfeerfoto (glas op maat). Leeg = tot je een eigen/Pexels-foto plaatst. */}
          {/* Dit is doorgaans de LCP-afbeelding, dus élke byte hier telt direct mee in de score.
              GEEN AVIF meer (01-08-2026 verwijderd): op mobiel laadde de AVIF-bron wel (bytes
              binnen, img.complete=true) maar tekende 'm nooit — de LQIP-vaagvlek uit HERO_LQIP
              bleef zichtbaar, permanent, voor elke mobiele bezoeker. WebP is 100% betrouwbaar en
              scheelt in de praktijk maar een fractie t.o.v. AVIF; een kapotte hero weegt zwaarder
              dan een paar KB besparing. srcSet: zonder responsive maten stuurden we iedereen (ook
              mobiel) de volle 1200x1600-bronfoto, terwijl het vak op mobiel nooit breder is dan het
              scherm. 480/700/900w dekken mobiel/tablet/desktop-kolom, 1200 blijft de fallback voor
              grote schermen. De 700w-stap vult het gat tussen 480 en 900: op veel telefoons (DPR
              ~1,6-2,6) is de benodigde rasterbreedte ~600-700px, en zonder die stap pakte de
              browser steeds de 900w-versie. */}
          {rightImg && (
            <img
              src={rightImg}
              srcSet={`${rightImg.replace(/\.webp$/, "-480.webp")} 480w, ${rightImg.replace(/\.webp$/, "-700.webp")} 700w, ${rightImg.replace(/\.webp$/, "-750.webp")} 750w, ${rightImg.replace(/\.webp$/, "-900.webp")} 900w, ${rightImg} 1200w`}
              sizes="(min-width: 768px) 780px, 100vw"
              width={1200}
              height={1600}
              alt={t("Glaszetter maakt glas op maat")}
              className="absolute inset-0 h-full w-full object-cover"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          {/* Zwevend wit info-vak linksonder (Coolblue-stijl) — hoogte volgt de inhoud */}
          <div className="absolute inset-x-4 bottom-4 md:inset-x-auto md:left-8 md:bottom-8 md:w-[58%] md:max-w-lg">
            <div className="flex flex-col gap-2.5 bg-white px-5 py-5 shadow-2xl sm:gap-3 sm:px-9 sm:py-8">
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-accent-700">{rightEyebrow}</p>
                <h2 className="text-3xl font-extrabold leading-[1.02] tracking-tight text-[#013878] sm:text-4xl">
                  {rightTitle}<span className="text-accent-500">.</span>
                </h2>
                <p className="mt-1 text-xl font-bold leading-tight text-primary-600 sm:text-2xl">{rightSubhead}</p>
              </div>

              <p className="text-sm leading-snug text-primary-600">{rightDesc}</p>

              <div className="pt-1">
                <Link
                  to={rightCtaTo}
                  className="inline-flex items-center gap-2 bg-accent-700 px-5 py-2.5 text-xs font-bold uppercase tracking-wide text-white transition hover:bg-accent-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 sm:px-6 sm:py-3"
                >
                  {rightCtaLabel}
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Secundair blok — vaste categorie-promo (kitten): navy vlak + foto + tekst.
            Mobiel = tweede carousel-slide (zelfde hoogte), desktop volle hoogte. */}
        <div className="relative h-[420px] w-full shrink-0 snap-start overflow-hidden bg-[#013878] sm:h-[clamp(480px,58vh,680px)] md:h-auto md:w-auto md:shrink">
          {LEFT_PROMO.image && (
            <img
              src={LEFT_PROMO.image}
              // Zelfde maten-truc als het glasblok hiernaast: zonder srcSet krijgt ook een telefoon
              // de volle 1448px-bron, terwijl het vak daar nooit breder is dan het scherm.
              srcSet={`/hero-werkbank-480.webp 480w, /hero-werkbank-700.webp 700w, /hero-werkbank-750.webp 750w, /hero-werkbank-900.webp 900w, ${LEFT_PROMO.image} 1448w`}
              sizes="(min-width: 768px) 780px, 100vw"
              width={1448}
              height={1086}
              // Beschrijvende alt: dit is de eigen productfoto van het promoblok, geen
              // achtergrondversiering — hij toont waar de knop naartoe leidt.
              alt={`${leftTitle} — ${t("glaszuiger, glassnijder, plamuurmes en beglazingsblokjes bij Glastotaal")}`}
              // Mobiel is het vak smal en hoog: het uitsnijpunt iets naar rechts houdt het
              // gereedschap in beeld in plaats van alleen het lege werkblad.
              className="absolute inset-0 h-full w-full object-cover object-[72%_50%] md:object-center"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )}
          <div className="relative flex h-full min-h-[240px] flex-col justify-between p-5 sm:p-8 md:min-h-0 lg:p-9 [text-shadow:0_1px_10px_rgba(1,56,120,0.55)]">
            <div>
              <h2 className="text-2xl font-extrabold leading-[1.04] tracking-tight text-white sm:text-4xl">
                {leftTitle}<span className="text-accent-500">.</span>
              </h2>
              <p className="mt-1 text-lg font-bold leading-tight text-white/85 sm:text-2xl">{leftSub}</p>
              {leftChip && (
                <span className="mt-2 inline-block bg-accent-700 px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-white sm:mt-4">{leftChip}</span>
              )}
            </div>
            {/* Linksonder de tekstlink (geen knop) — de reviewsterren en de vanaf-prijs stonden hier
                ook; beide eruit per 20-09-2026 (besluit Thijs). */}
            <Link to={leftCtaTo} className="inline-flex w-max items-center gap-2 text-base font-bold text-white transition hover:gap-3">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-accent-500">
                <path d="M9 6l6 6-6 6" />
              </svg>
              {leftCtaLabel}
            </Link>
          </div>
        </div>
      </div>

      {/* Progress-balkjes (Coolblue-stijl) — alleen mobiel, tik = naar die slide */}
      <div className="mt-2.5 flex gap-2 md:hidden" role="tablist" aria-label={t("Hero-slides")}>
        {[0, 1].map((i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={slide === i}
            aria-label={`Slide ${i + 1}`}
            onClick={() => trackRef.current?.scrollTo({ left: i * trackRef.current.clientWidth, behavior: "smooth" })}
            className={`h-1 flex-1 rounded-full transition-colors ${slide === i ? "bg-[#013878]" : "bg-primary-100"}`}
          />
        ))}
      </div>
    </section>
  );
}
